const AUTHORITY = 'https://login.microsoftonline.com/common/oauth2/v2.0';
const SCOPE = 'openid profile email';
const FAILED = 'Microsoft sign-in failed';

export const isEmbeddedWebView = userAgent => /median|gonative/i.test(userAgent || '');

const microsoftRedirectUri = () => `${window.location.origin}/signin`;

export const isMicrosoftSignInPopup = () =>
  !!window.opener && /[#&]state=/.test(window.location.hash) && /[#&](code|error)=/.test(window.location.hash);

const base64Url = bytes =>
  btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const randomString = () => base64Url(crypto.getRandomValues(new Uint8Array(32)));

const pkceChallenge = async verifier =>
  base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));

const tokenNonce = (idToken) => {
  try {
    const payload = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)).nonce;
  } catch (e) {
    return null;
  }
};

const cancelled = () => Object.assign(new Error('Microsoft sign-in was cancelled'), { cancelled: true });

const waitForRedirect = popup =>
  new Promise((resolve, reject) => {
    const timer = setInterval(() => {
      if (popup.closed) {
        clearInterval(timer);
        reject(cancelled());
        return;
      }
      let href;
      try {
        ({ href } = popup.location);
      } catch (e) {
        return;
      }
      if (!href.startsWith(microsoftRedirectUri())) return;
      clearInterval(timer);
      const params = new URLSearchParams(popup.location.hash.slice(1));
      popup.close();
      resolve(params);
    }, 250);
  });

export async function signInWithMicrosoft(clientId) {
  const popup = window.open('', 'mpower-microsoft-signin', 'width=500,height=640');
  if (!popup) throw new Error('Allow pop-ups for this site to sign in with Microsoft');
  const state = randomString();
  const nonce = randomString();
  const verifier = randomString();
  const redirectUri = microsoftRedirectUri();
  const query = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    redirect_uri: redirectUri,
    response_mode: 'fragment',
    scope: SCOPE,
    state,
    nonce,
    code_challenge: await pkceChallenge(verifier),
    code_challenge_method: 'S256',
    prompt: 'select_account'
  });
  popup.location.href = `${AUTHORITY}/authorize?${query}`;
  const params = await waitForRedirect(popup);
  if (params.get('error') === 'access_denied') throw cancelled();
  const code = params.get('code');
  if (params.get('state') !== state || !code) throw new Error(FAILED);
  const res = await fetch(`${AUTHORITY}/token`, {
    method: 'POST',
    body: new URLSearchParams({
      client_id: clientId,
      grant_type: 'authorization_code',
      code,
      redirect_uri: redirectUri,
      code_verifier: verifier,
      scope: SCOPE
    })
  });
  const idToken = res.ok ? (await res.json()).id_token : null;
  if (!idToken || tokenNonce(idToken) !== nonce) throw new Error(FAILED);
  return idToken;
}
